import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

interface LiveFrameProps {
  /** Snapshot URL without query string, e.g. `${API_BASE}cameras/CAM-0-24/snapshot.jpg` */
  src: string;
  alt?: string;
  className?: string;
  /** Upper limit of pictures per second (a slow link simply gets fewer). */
  maxFps?: number;
  /** "tile": a small cell on a video wall. Smaller pictures only, so that many views at once stay light. */
  size?: 'full' | 'tile';
  /** Called when no picture has arrived for a long while (about 20 tries in a row). The view keeps trying. */
  onError?: () => void;
}

/**
 * Live camera view that never falls behind.
 *
 * Use it only for a view the operator opens for one camera (popup, enlarged tile): every picture request counts
 * as somebody watching, so the analytics service draws every frame of that camera while it is open. Lists, walls
 * and grids show the saved picture instead (SavedFrame, a new one every 10 minutes): with the Video Wall polling
 * every tile live, the Antharalayam door cameras fell to ~1 analysed frame per second (13 Oct 2026).
 *
 * An MJPEG stream pushes every frame; on a link slower than the stream the frames queue up on the
 * way and the picture runs further and further behind the camera. This view instead loads the
 * newest picture up to `maxFps` times a second, with never more than two on the way. A slow link
 * therefore shows fewer pictures per second, but each of them is current.
 *
 * A request that gets no answer is given up after a few seconds: on a link that stalls (VPN), a request
 * can otherwise hang for minutes, and two hanging requests would freeze the view until the page is reopened.
 *
 * Picture size follows the link: when pictures take long to arrive it asks for smaller ones
 * (more pictures per second, less detail), and goes back up when the link is fast.
 */
// [width, JPEG quality], largest first: about 65 / 33 / 21 / 11 KB for a queue camera
const LEVELS: [number, number][] = [[800, 55], [640, 45], [512, 40], [400, 30]];
export const LiveFrame = forwardRef<HTMLImageElement, LiveFrameProps>(function LiveFrame(
  { src, alt, className, maxFps = 12, size = 'full', onError }, ref,
) {
  const imgRef = useRef<HTMLImageElement>(null);
  useImperativeHandle(ref, () => imgRef.current as HTMLImageElement);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastUrl = '';
    let failures = 0;
    const ctrl = new AbortController();
    const gap = 1000 / Math.max(1, maxFps);
    const top = size === 'tile' ? 2 : 0;   // a tile never asks for more than 512 px
    let level = top;        // start with the largest picture allowed; drops only if the link is slow
    let avg = 0;            // smoothed time one picture takes to arrive, ms
    let sinceChange = 0;
    let inflight = 0;       // at most 2 pictures on the way: the next is asked for while one is still arriving
    let seq = 0;            // order the pictures were asked in
    let shown = 0;          // newest picture on screen; an older one that arrives late is dropped

    const load = async () => {
      const mine = ++seq;
      const t0 = performance.now();
      inflight += 1;
      const one = new AbortController();                 // this request only: stopped after 6 s without an answer
      const giveUp = setTimeout(() => one.abort(), 6000);
      const stopAll = () => one.abort();
      ctrl.signal.addEventListener('abort', stopAll);
      try {
        const [width, quality] = LEVELS[level];
        const r = await fetch(`${src}?w=${width}&q=${quality}&t=${Date.now()}`, { signal: one.signal, cache: 'no-store' });
        if (!r.ok) throw new Error(String(r.status));
        const blob = await r.blob();
        if (!alive || mine < shown) return;
        const url = URL.createObjectURL(blob);
        if (imgRef.current) imgRef.current.src = url;
        if (lastUrl) URL.revokeObjectURL(lastUrl);
        lastUrl = url;
        shown = mine;
        failures = 0;
        const took = performance.now() - t0;
        avg = avg ? avg * 0.7 + took * 0.3 : took;
        sinceChange += 1;
        if (sinceChange >= 8) {
          // pictures arriving slowly: ask for smaller ones; comfortably fast: larger ones
          if (avg > 220 && level < LEVELS.length - 1) { level += 1; sinceChange = 0; avg = 0; }
          else if (avg < 90 && level > top) { level -= 1; sinceChange = 0; avg = 0; }
        }
      } catch {
        if (!alive) return;
        failures += 1;
        if (failures === 20) onErrorRef.current?.();
        if (failures === 3 && level < LEVELS.length - 1) level = LEVELS.length - 1;   // struggling link: smallest pictures
      } finally {
        clearTimeout(giveUp);
        ctrl.signal.removeEventListener('abort', stopAll);
        inflight -= 1;
      }
    };

    const tick = () => {
      if (!alive) return;
      // Page reported as hidden: keep going at one picture a second instead of stopping. Browsers also report
      // "hidden" for a window that is covered by another one, on a video wall or seen through remote desktop,
      // and a stopped view there looks like a camera that does not stream. After failures: slow down too.
      if (inflight < 2) load();
      timer = setTimeout(tick, failures || document.hidden ? 1000 : gap);
    };
    tick();
    return () => {
      alive = false;
      ctrl.abort();
      if (timer) clearTimeout(timer);
      if (lastUrl) URL.revokeObjectURL(lastUrl);
    };
  }, [src, maxFps, size]);

  // an <img> with no picture yet shows the browser's broken-image icon and the alt text: start with an empty
  // 1x1 picture, which is replaced by the first frame
  return <img ref={imgRef} alt={alt} className={className}
    src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==" />;
});
